import { Box, Button, ButtonDropdown, Container, ContentLayout, Header, Link, Select, SpaceBetween, StatusIndicator, Tabs } from "@amzn/awsui-components-react";
import React, { useState, useEffect, Suspense } from "react";
import { useSelector } from "react-redux";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from '../../../utilities/adminUtils';
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useAuth } from "src/context/AuthContextProvider";
import { useDirectReports } from "src/hooks/useSuggestedLogins";
import CPBoardView from "../components/CPBoardView";
import Apps from "src/Home/Apps";
import TeamApplications from "src/Home/TeamApplications";
import CustomMultipleResourcePage from "../components/CustomMultipleResourcePage";
import { AI_AGENT_ENABLED } from "src/Ateam-BI/AutoMate/constants";
import useAgentChat from "src/Ateam-BI/AutoMate/hooks/useAgentChat";
import useSearchAgent from "src/Ateam-BI/AutoMate/hooks/useSearchAgent";
import useAgentSuggestions from "src/Ateam-BI/AutoMate/hooks/useAgentSuggestions";
import { usePersonaContext } from "src/Ateam-BI/AutoMate/context/PersonaContext";
import { createClosePortalOwner, updateClosePortalOwner, getOwnerDetails, createCustomTabOfOwner, updateCustomTabOfOwner, deleteCustomTabOfOwner } from "src/utilities/CloseAPIs";
import { configureGenericAppSync } from "src/context/AuthContextUtility";
import { GET_OWNERS_CACHE_KEY } from "src/AppConstants/CacheConstants";
import { closePortalDefaultTabs, SIDE_NAV_TEAM_LABELS } from "../constants";
import { v4 as uuidv4 } from 'uuid';
import { createPreference, updatePreference } from "src/utilities/AppSyncPlusAPIs";
import CreateTeamModal from "src/Home/CreateTeamModal";
import CustomizeTabsModal from "src/Home/CustomizeTabsModal";

// Lazy-loaded AI components — only fetched when AI_AGENT_ENABLED is true
const AISearchBar = React.lazy(() => import('src/Ateam-BI/AutoMate/components/AISearchBar/AISearchBar'));
const SearchPopup = React.lazy(() => import('src/Ateam-BI/AutoMate/components/SearchPopup/SearchPopup'));
const AutoMateBubble = React.lazy(() => import('src/Ateam-BI/AutoMate/components/AutoMateBubble/AutoMateBubble'));

// Simple error boundary so a failed lazy import doesn't crash the whole page
class AIErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('AIErrorBoundary caught:', error, info); }
  render() { return this.state.hasError ? null : this.props.children; }
}

export const MyTeamsPage = ({selectedCPView, setHomeCPView, setSelectedCPView, setVisible, visible, cpOwnerOptions, viewAccess, addOrEditAccess, tabs, allTabs, allCustomTabs, hiddenTabs, regions, organizations, isAteamBI, orderedTabs, tabOrderLoaded, updateTab, resetHref, refreshOwners, refreshTabConfig, ownerTabConfigInfo, currentTabPreferences, setOwnerTabConfigInfo, setTabs, setAllTabs, setAllCustomTabs, setHiddenTabs, setCurrentTabPreferences}) => {
    const history = useHistory();
    const ln = useLocation()
    const userAuthenticationDetails = useAuth();
    const firstName = userAuthenticationDetails.GivenName;
    const currentUser = useSelector((globalState) => globalState.auth.userId)
    const currentStage = useSelector((globalState) => globalState.auth.currentStage)
    const [activeTabId, setActiveTabId] = useState();
    const [activeHref, setActiveHref] = useState()
    const [customizeTabsVisible, setCustomizeTabsVisible] = useState(false)

    const isPortalAdmin = useSelector(selectIsEffectiveAdmin)
    const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin)

    const [errorText, setErrorText] = useState()
    const [selectedTabControlsSegmentId, setSelectedTabControlsSegmentId] = useState("add")
    const [selectedUpdateOrDeleteTab, setSelectedUpdateOrDeleteTab] = useState()
    const [selectedTabToUnhide, setSelectedTabToUnhide] = useState()
    const [updateTabNameInput, setUpdateTabNameInput] = useState('')
    const [newTabName, setNewTabName] = useState('')
    const [inIframe, setInIframe] = useState(window.self !== window.top)
    const [modalLoading, setModalLoading] = useState(false)
    const [selectedRegions, setSelectedRegions] = useState([])
    const [selectedOrgs, setSelectedOrgs] = useState([])
    const [selectedVP, setSelectedVP] = useState()
    const [newTeamName, setNewTeamName] = useState('')
    const [newTeamLDAP, setNewTeamLDAP] = useState('')
    const { directReports, directReportsLoading, getDirectReportsCaller } = useDirectReports();
    

    const [selectedTeamToEdit, setSelectedTeamToEdit] = useState(null)
    const [selectedTabType, setSelectedTabType] = useState()

    const [aiSearchValue, setAiSearchValue] = useState("")
    const [aiPopupVisible, setAiPopupVisible] = useState(false)
    const [chatBubbleOpen, setChatBubbleOpen] = useState(false)
    const [selectedSuggestion, setSelectedSuggestion] = useState("")
    const { messages: aiMessages, isStreaming: aiIsStreaming, waitingReason: aiWaitingReason, handleChatSubmit: aiHandleChatSubmit, handleCancel: aiHandleChatCancel, handleNewChat: aiHandleNewChat } = useAgentChat();
    // Hoisted search-agent store. Owns the search results so they survive
    // closing/reopening the popup (the popup itself is presentational now).
    const {
        query: aiSearchQuery,
        searchAnswer: aiSearchAnswer,
        searchSources: aiSearchSources,
        quickSearchResults: aiQuickSearchResults,
        isSearching: aiIsSearching,
        searchError: aiSearchError,
        searchSessionId: aiSearchSessionId,
        searchConversationId: aiSearchConversationId,
        runSearch: aiRunSearch,
    } = useSearchAgent();
    // Dynamic suggestions for the AISearchBar. The hook owns the 1000ms
    // debounce, the searchAgent call, the onAgentResponse subscription, and
    // the LRU cache. Its conversationId is independent of the SearchPopup's
    // primary `searchAgent` call (Requirement 10.9) because the popup uses
    // `useAgentChat` (sendAgentMessage) and this hook fires its own
    // searchAgent invocations per debounce — no shared conversationId.
    const { suggestions: aiSuggestions, isLoading: aiSuggestionsLoading, onQueryChange: aiOnQueryChange } = useAgentSuggestions();
    // Use the shared persona context instead of calling usePersona directly
    // to avoid duplicate API calls (PersonaProvider already owns the fetch).
    const { personaText: aiPersonaText, userName: aiUserName } = usePersonaContext();

    const handleAIAsk = (query) => {
        // Every submission — typed (single or multi-word) or selected from
        // the autocomplete — fires both the search (`aiRunSearch`) and the
        // chatbot (`aiHandleChatSubmit`). The SearchPopup hosts both UIs.
        // Search state lives in `useSearchAgent` (above the popup) so results
        // persist across close/reopen without re-firing.
        setChatBubbleOpen(false);
        setSelectedSuggestion(query);
        setAiPopupVisible(true);
        aiRunSearch(query);
        aiHandleChatSubmit(query);
    }

    const resetModalParams = () => {
      setVisible(false)
      setCustomizeTabsVisible(false)
      setModalLoading(false)
      setErrorText()
      setSelectedTabControlsSegmentId("add")
      setNewTabName('')
      setSelectedUpdateOrDeleteTab()
      setUpdateTabNameInput('')
      setSelectedTabToUnhide()
    }

    const resetCreateTeamModal = () => {
      setModalLoading(false)
      setNewTeamName('')
      setNewTeamLDAP('')
      setSelectedOrgs([])
      setSelectedRegions([])
      setSelectedVP(null)
      setSelectedTeamToEdit(null)
      setErrorText(null)
      resetModalParams()
    }

    const addOrUpdateTabConfig = async (tabConfig) => {
      if (selectedCPView) {
        const newPreferenceId = uuidv4();
        const information = { "closePortalOwner": selectedCPView?.value, "tabConfiguration": { "closePortalTabs": tabConfig } }
        const label = `Tab configuration for Close Portal Page ${selectedCPView?.label}`
        if (ownerTabConfigInfo?.length <= 0) {
          createPreference(
            newPreferenceId,
            'CLOSE_PORTAL_TABS_CONFIG',
            label,
            selectedCPView?.value,
            'CLOSE_PORTAL',
            currentUser,
            information,
            null,
            (response) => {
              const newPreference = {
                preferenceId: newPreferenceId,
                label: label,
                associationId: selectedCPView?.value,
                associationIdType: 'CLOSE_PORTAL',
                preferenceType: 'CLOSE_PORTAL_TABS_CONFIG',
                createdBy: currentUser,
                lastUpdatedBy: currentUser,
                lastUpdatedTimestamp: Date.now().toString(),
                information: information
              };
              setCurrentTabPreferences([newPreference]);
              const info = newPreference?.information
              if (info?.tabConfiguration?.closePortalTabs) {
                const tabConfigInfo = info?.tabConfiguration?.closePortalTabs
                setOwnerTabConfigInfo(tabConfigInfo)
                const visibleTabs = tabConfigInfo?.filter((tab) => tab?.visible)
                setTabs(visibleTabs)
                setAllTabs(tabConfigInfo)
                const customTabs = tabConfigInfo?.filter((tab) => tab?.isDefault === false)
                setAllCustomTabs(customTabs)
                const hiddenTabsList = tabConfigInfo?.filter((tab) => tab?.visible === false)
                setHiddenTabs(hiddenTabsList)
              }
            },
            (error) => {
              console.error('Error creating preferences:', error);
              const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to save preferences";
              setErrorText(errorMessage);
              setModalLoading(false);
            }
          );
        } else {
          const currentPreferenceId = currentTabPreferences[0]?.preferenceId
          updatePreference(
            currentPreferenceId,
            label,
            currentUser,
            information,
            (response) => {
              const newPreference = {
                preferenceId: currentPreferenceId,
                label: label,
                associationId: selectedCPView?.value,
                associationIdType: 'CLOSE_PORTAL',
                preferenceType: 'CLOSE_PORTAL_TABS_CONFIG',
                createdBy: currentUser,
                lastUpdatedBy: currentUser,
                lastUpdatedTimestamp: Date.now().toString(),
                information: information
              };
              setCurrentTabPreferences([newPreference]);
              const info = newPreference?.information
              if (info?.tabConfiguration?.closePortalTabs) {
                const tabConfigInfo = info?.tabConfiguration?.closePortalTabs
                setOwnerTabConfigInfo(tabConfigInfo)
                const visibleTabs = tabConfigInfo?.filter((tab) => tab?.visible)
                setTabs(visibleTabs)
                setAllTabs(tabConfigInfo)
                const customTabs = tabConfigInfo?.filter((tab) => tab?.isDefault === false)
                setAllCustomTabs(customTabs)
                const hiddenTabsList = tabConfigInfo?.filter((tab) => tab?.visible === false)
                setHiddenTabs(hiddenTabsList)
              }
            },
            (error) => {
              console.error('Error updating preferences:', error);
              const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to save preferences";
              setErrorText(errorMessage);
              setModalLoading(false);
            }
          )
        }
      }
    }

    const hideOrUnhideCustomTab = async (tabId, hide) => {
      if (selectedCPView?.value && tabId) {
        const tabConfig = allTabs?.map((tab) => {
          if (tab?.id === tabId) {
            return { ...tab, visible: !hide }
          }
          return tab
        })
        addOrUpdateTabConfig(tabConfig)
      }
    }

    const addNewCustomTab = async () => {
      if (newTabName?.trim() !== '' && selectedTabType) {
        if (allCustomTabs?.length >= 10) {
          setErrorText('Cannot create more than 10 custom tabs!')
          return;
        } else if (allTabs?.some(tab => tab.id === newTabName?.trim() || tab.label === newTabName?.trim())) {
          setErrorText('A tab with same name exists!')
          return;
        } else {
          setModalLoading(true)
          await createCustomTabOfOwner(newTabName?.trim(), selectedCPView?.value, currentUser, selectedTabType?.value, function onSuccess(result) {
            
            const tabConfig = allTabs?.concat({
              "id": newTabName?.trim(),
              "label": newTabName?.trim(),
              "isDefault": false,
              "order": closePortalDefaultTabs?.length + 1,
              "tabType": selectedTabType?.value,
              "visible": true
            })
            addOrUpdateTabConfig(tabConfig)
            resetModalParams()
            configureGenericAppSync()
          }, function onFailure(response) {
            setModalLoading(false)
            setErrorText('Failed to create tab. Please try again.')
            configureGenericAppSync()
          })
        }
      } else {
        setErrorText('All fields are required!')
        return;
      }
    }

    const updateExistingCustomTab = async () => {
      if (selectedUpdateOrDeleteTab) {
        if (updateTabNameInput?.trim() === '') {
          setErrorText('Please provide a new tab name to update!')
          return;
        } else if (allTabs?.some(tab => tab.id === updateTabNameInput?.trim() || tab.label === updateTabNameInput?.trim())) {
          setErrorText('A tab with same name exists!')
          return;
        } else {
          setModalLoading(true)

          // For default tabs, only update label in DynamoDB — skip the backend RDS call
          const selectedTab = allTabs?.find(tab => tab?.id === selectedUpdateOrDeleteTab?.value);
          if (selectedTab?.isDefault) {
            const tabConfig = allTabs?.map((tab) => {
              if (selectedUpdateOrDeleteTab?.value === tab?.id) {
                return {
                  ...tab,
                  "label": updateTabNameInput?.trim(),
                }
              } else {
                return tab
              }
            })
            addOrUpdateTabConfig(tabConfig)
            resetModalParams()
          } else {
            await updateCustomTabOfOwner(selectedCPView?.value, selectedUpdateOrDeleteTab?.value, updateTabNameInput?.trim(), function onSuccess(result) {
              
              const tabConfig = allTabs?.map((tab) => {
                if (selectedUpdateOrDeleteTab?.value === tab?.id) {
                  return {
                    ...tab,
                    "id": updateTabNameInput?.trim(),
                    "label": updateTabNameInput?.trim(),
                  }
                } else {
                  return tab
                }
              })

              addOrUpdateTabConfig(tabConfig)
              resetModalParams()
              configureGenericAppSync()
            }, function onFailure(response) {
              setModalLoading(false)
              setErrorText('Failed to update tab. Please try again.')
              configureGenericAppSync()
            })
          }
        }
      } else {
        setErrorText('Please select a tab to update!')
        return;
      }
    }

    const deleteExistingCustomTab = async () => {
      if (selectedUpdateOrDeleteTab) {
        setModalLoading(true)
        await deleteCustomTabOfOwner(selectedCPView?.value, selectedUpdateOrDeleteTab?.value, function onSuccess(result) {

          const tabConfig = allTabs?.filter((tab) => (selectedUpdateOrDeleteTab?.value !== tab?.id))

          addOrUpdateTabConfig(tabConfig)
          resetModalParams()
          setActiveTabId("HOME")
          configureGenericAppSync()
        }, function onFailure(response) {
          setModalLoading(false)
          setErrorText('Failed to delete tab. Please try again.')
          configureGenericAppSync()
        })

      } else {
        setErrorText('Please select a tab to delete!')
        return;
      }
    }

    const handleReorderTabs = async (newOrder) => {
      if (selectedCPView?.value) {
        try {
          setModalLoading(true);

          const fixedTabsConfig = [{
            "id": "HOME",
            "label": "Home",
            "isDefault": true,
            "order": 1,
            "tabType": "BOARD",
            "visible": true
          }]
          const tabConfig = fixedTabsConfig?.concat(newOrder)

          await addOrUpdateTabConfig(tabConfig)

          setCustomizeTabsVisible(false);
          setModalLoading(false);
          setErrorText();
          setSelectedTabControlsSegmentId("add");

        } catch (error) {
          console.error('Failed to save tab order:', error);
          setErrorText('Failed to save tab order. Please try again.');
          setModalLoading(false);
        }
      }
    };

    const createNewOwner = async (ownerType = 'TEAM') => {
      const missingFields = [];
      if (!newTeamName?.trim()) missingFields.push('Team Display name');
      if (!newTeamLDAP?.trim()) missingFields.push('LDAP');
      if (!selectedOrgs || selectedOrgs.length === 0) missingFields.push('Organization');
      if (!selectedRegions || selectedRegions.length === 0) missingFields.push('Region');
      if (!selectedVP) missingFields.push('VP');

      if (missingFields.length > 0) {
        setErrorText(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      setModalLoading(true)
      let regionsVal = []
      let orgs = []
      let vps = []

      if (selectedOrgs?.length >= 1) {
        orgs = selectedOrgs?.map((org) => (org?.value))
      }
      if (selectedRegions?.length >= 1) {
        regionsVal = selectedRegions?.map((region) => (region?.value))
      }
      if (selectedVP) {
        vps = [selectedVP?.value]
      }

      await createClosePortalOwner(newTeamName?.trim(), newTeamLDAP?.trim(), ownerType, currentUser, orgs, regionsVal, vps, function onSuccess(result) {
        localStorage.removeItem(GET_OWNERS_CACHE_KEY);
        refreshOwners()
        resetCreateTeamModal()
        configureGenericAppSync()
      }, function onFailure(response) {
        setErrorText(response)
        setModalLoading(false)
        configureGenericAppSync()
      })
    }

    const updateTeam = async () => {
      const missingFields = [];
      if (!selectedTeamToEdit) missingFields.push('Team selection');
      if (!newTeamName?.trim()) missingFields.push('Team Display name');
      if (!newTeamLDAP?.trim()) missingFields.push('LDAP');
      if (!selectedOrgs || selectedOrgs.length === 0) missingFields.push('Organization');
      if (!selectedRegions || selectedRegions.length === 0) missingFields.push('Region');
      if (!selectedVP) missingFields.push('VP');

      if (missingFields.length > 0) {
        setErrorText(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      setModalLoading(true);
      let regionsVal = [];
      let orgs = [];
      let vps = [];

      if (selectedOrgs?.length >= 1) {
        orgs = selectedOrgs?.map((org) => (org?.value));
      }
      if (selectedRegions?.length >= 1) {
        regionsVal = selectedRegions?.map((region) => (region?.value));
      }
      if (selectedVP) {
        vps = [selectedVP?.value];
      }

      await updateClosePortalOwner(
        selectedTeamToEdit.value,
        newTeamName?.trim(),
        newTeamLDAP?.trim(),
        orgs,
        regionsVal,
        vps,
        currentUser,
        function onSuccess(result) {
          localStorage.removeItem(GET_OWNERS_CACHE_KEY);
          refreshOwners();
          resetCreateTeamModal();
          configureGenericAppSync();
        },
        function onFailure(response) {
          setErrorText(response || 'Failed to update team');
          setModalLoading(false);
          configureGenericAppSync();
        }
      );
    }

    const sanitizeResponseData = (data) => {
      if (!data || typeof data !== 'string') return data;
      try {
        let sanitized = data;
        sanitized = sanitized.replace(/(\w+)=/g, '"$1":');
        sanitized = sanitized.replace(/:\s*\[([^\]]*)\]/g, (match, arrayContent) => {
          if (!arrayContent.trim()) return ': []';
          const elements = [];
          let currentElement = '';
          let inQuotes = false;
          for (let i = 0; i < arrayContent.length; i++) {
            const char = arrayContent[i];
            if (char === '"' && (i === 0 || arrayContent[i - 1] !== '\\')) {
              inQuotes = !inQuotes;
              currentElement += char;
            } else if (char === ',' && !inQuotes) {
              if (currentElement.trim()) elements.push(currentElement.trim());
              currentElement = '';
            } else {
              currentElement += char;
            }
          }
          if (currentElement.trim()) elements.push(currentElement.trim());
          const processedElements = elements.map(element => {
            const trimmed = element.trim();
            if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed;
            if (/^[\d.]+$/.test(trimmed)) return trimmed;
            if (/^(true|false|null)$/i.test(trimmed)) return trimmed.toLowerCase();
            return `"${trimmed}"`;
          });
          return `: [${processedElements.join(', ')}]`;
        });
        sanitized = sanitized.replace(/:\s*([^,\[\]{}"\d\s][^,\[\]{}]*?)(?=[,\]}])/g, (match, value) => {
          const trimmedValue = value.trim();
          if (trimmedValue.startsWith('"') || /^[\d.]+$/.test(trimmedValue) || /^(true|false|null)$/i.test(trimmedValue) || trimmedValue.startsWith('[') || trimmedValue.startsWith('{')) return `: ${trimmedValue}`;
          return `: "${trimmedValue}"`;
        });
        sanitized = sanitized.replace(/:\s*([^,\[\]{}"][^,\[\]{}]*[^,\[\]{}"])(?=[,\]}])/g, (match, value) => {
          const trimmedValue = value.trim();
          if (!trimmedValue.startsWith('"') && !trimmedValue.endsWith('"') && !/^[\d.]+$/.test(trimmedValue) && !/^(true|false|null)$/i.test(trimmedValue)) return `: "${trimmedValue}"`;
          return `: ${trimmedValue}`;
        });
        return JSON.parse(sanitized);
      } catch (error) {
        try {
          let fallbackSanitized = data
            .replace(/(\w+)=/g, '"$1":')
            .replace(/:\s*([^,\[\]{}]+?)(?=[,\]}])/g, (match, value) => {
              const trimmed = value.trim();
              if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                const arrayContent = trimmed.slice(1, -1);
                if (!arrayContent.trim()) return ': []';
                const elements = arrayContent.split(',').map(el => {
                  const element = el.trim();
                  if (/^[\d.]+$/.test(element) || /^(true|false|null)$/i.test(element)) return element;
                  return `"${element}"`;
                });
                return `: [${elements.join(', ')}]`;
              }
              if (/^[\d.]+$/.test(trimmed) || /^(true|false|null)$/i.test(trimmed)) return `: ${trimmed}`;
              return `: "${trimmed}"`;
            });
          return JSON.parse(fallbackSanitized);
        } catch (fallbackError) {
          console.error('Fallback sanitization also failed:', fallbackError);
          throw new Error('Invalid data format received from server - unable to parse');
        }
      }
    };

    const loadTeamDetails = async (teamId, retryCount = 0) => {
      setModalLoading(true);
      setErrorText('');

      await getOwnerDetails(teamId, function onSuccess(result) {
        try {
          const data = result.data.getOwnerDetailsRDS;
          if (!data) {
            setErrorText('No team data found');
            configureGenericAppSync();
            setModalLoading(false);
            return;
          }

          let teamDetails;
          if (typeof data === 'string') {
            if (data.trim() === '' || data.trim() === 'null' || data.trim() === 'undefined') {
              setErrorText('Invalid team data format');
              configureGenericAppSync();
              setModalLoading(false);
              return;
            }
            try {
              teamDetails = JSON.parse(data);
            } catch (parseError) {
              try {
                teamDetails = sanitizeResponseData(data);
              } catch (sanitizeError) {
                setErrorText('Failed to parse team data - invalid format received from server');
                configureGenericAppSync();
                setModalLoading(false);
                return;
              }
            }
          } else if (Array.isArray(data)) {
            teamDetails = data;
          } else if (data && typeof data === 'object') {
            teamDetails = [data];
          } else {
            setErrorText('Unexpected team data format');
            configureGenericAppSync();
            setModalLoading(false);
            return;
          }

          if (teamDetails && Array.isArray(teamDetails) && teamDetails.length > 0) {
            const team = teamDetails[0];
            setNewTeamName(team.owner_name || '');
            setNewTeamLDAP(team.owner_identifier || '');

            if (team.organizations !== undefined && team.organizations !== null) {
              try {
                let orgData = team.organizations;
                if (typeof orgData === 'string' && orgData.trim() !== '') orgData = JSON.parse(orgData);
                if (Array.isArray(orgData)) {
                  const teamOrgs = orgData.map(org => organizations?.find(o => o.value === org) || { label: org, value: org }).filter(Boolean);
                  setSelectedOrgs(teamOrgs);
                } else {
                  setSelectedOrgs([]);
                }
              } catch (e) {
                setSelectedOrgs([]);
              }
            } else {
              setSelectedOrgs([]);
            }

            if (team.regions !== undefined && team.regions !== null) {
              try {
                let regionData = team.regions;
                if (typeof regionData === 'string' && regionData.trim() !== '') regionData = JSON.parse(regionData);
                if (Array.isArray(regionData)) {
                  const teamRegions = regionData.map(region => regions?.find(r => r.value === region) || { label: region, value: region }).filter(Boolean);
                  setSelectedRegions(teamRegions);
                } else {
                  setSelectedRegions([]);
                }
              } catch (e) {
                setSelectedRegions([]);
              }
            } else {
              setSelectedRegions([]);
            }

            if (team.vps !== undefined && team.vps !== null) {
              try {
                let vpData = team.vps;
                if (typeof vpData === 'string' && vpData.trim() !== '') vpData = JSON.parse(vpData);
                if (Array.isArray(vpData) && vpData.length > 0) {
                  const vp = directReports?.find(dr => dr.value === vpData[0]) || { label: vpData[0], value: vpData[0] };
                  setSelectedVP(vp);
                } else {
                  setSelectedVP(null);
                }
              } catch (e) {
                setSelectedVP(null);
              }
            } else {
              setSelectedVP(null);
            }
          } else {
            setErrorText('No team details found');
          }

          configureGenericAppSync();
          setModalLoading(false);
        } catch (error) {
          setErrorText('Failed to load team details: ' + error.message);
          configureGenericAppSync();
          setModalLoading(false);
        }
      }, function onFailure(response) {
        if (response && (
          response.message?.includes('ERR_SOCKET_NOT_CONNECTED') ||
          response.message?.includes('Network Error') ||
          response.message?.includes('ERR_NETWORK') ||
          response.networkError
        ) && retryCount < 2) {
          setErrorText(`Network error, retrying... (${retryCount + 1}/3)`);
          setTimeout(() => { loadTeamDetails(teamId, retryCount + 1); }, 1000 * (retryCount + 1));
          return;
        }
        if (retryCount >= 2) {
          setErrorText('Failed to load team details after multiple attempts. Please check your network connection and try again.');
        } else {
          setErrorText('Failed to load team details. Please try again.');
        }
        configureGenericAppSync();
        setModalLoading(false);
      });
    }

    const { tabIdFromURL, pageId } = useParams()

    const currentHour = (new Date()).getHours()

    const [greeting] = useState(() => {
        if (currentHour < 12) {
            return "Good Morning"
        } else if (currentHour < 18) {
            return "Good Afternoon"
        } else {
            return "Good Evening"
        }
    })

    useEffect(() => {
        if (ln.pathname?.toLowerCase()?.startsWith('/appslibrary')) {
            setActiveTabId('AppsLibrary')
        } else {
            let tabId = tabIdFromURL ? tabIdFromURL?.replaceAll('%2F', '/') : 'HOME';
            setActiveTabId(tabId)
        }
    }, [ln, tabIdFromURL])

    // If the active tab (e.g. HOME) is hidden, fall back to the first visible tab
    useEffect(() => {
        if (tabOrderLoaded && tabs?.length > 0 && activeTabId) {
            const isActiveTabVisible = tabs.some(t => t.id === activeTabId);
            if (!isActiveTabVisible) {
                const firstVisibleTab = tabs[0];
                if (firstVisibleTab) {
                    setActiveTabId(firstVisibleTab.id);
                    const teamId = selectedCPView?.value || pageId;
                    if (teamId) {
                        history.replace(`/ClosePortal/${firstVisibleTab.id?.replaceAll('/', '%2F')}/Page/${teamId}`);
                    }
                }
            }
        }
    }, [tabOrderLoaded, tabs, activeTabId]);


    useEffect(() => {
        setInIframe(window.self !== window.top)
    }, [])
    
    return (     
     <Box>
        <br></br>
        <ContentLayout
        >

          <Header
            actions={
              <></>
            }
          >

            <Box display={inIframe ? "none" : "block"}>
              <SpaceBetween direction="vertical" size="m">
                <Box
                  variant="h3"
                  padding={{ bottom: "s" }}
                  fontSize="heading-l"
                  color="inherit">{greeting}, {firstName} ! <>
                      <Box variant="h3"
                        padding="n"
                        fontSize="heading-l" display="inline">You are viewing the </Box>
                      {selectedCPView && cpOwnerOptions && cpOwnerOptions.length > 0 && (
                        <Box display="inline-block" margin={{ left: 'xs', right: 'xs' }}>
                          <Select
                            className="owner-select-dropdown"
                            selectedOption={selectedCPView}
                            onChange={({ detail }) => {
                              setHomeCPView(detail.selectedOption)
                              setSelectedCPView(detail.selectedOption)
                            }}
                            options={cpOwnerOptions?.filter(opt => !SIDE_NAV_TEAM_LABELS.includes(opt?.label))}
                            disabled={false}
                            filteringType="auto"
                          />
                        </Box>
                      )}
                      <Box variant="h3"
                        padding="n"
                        fontSize="heading-l" display="inline"> page </Box>
                      {addOrEditAccess && (
                        <Box display="inline-block" margin={{ left: 'xs' }}>
                          <Button
                            variant="icon"
                            iconName="settings"
                            onClick={() => { setVisible(true) }}
                            ariaLabel="Page settings"
                          />
                        </Box>
                      )}
                    </>
                  </Box>
              </SpaceBetween>
            </Box>
          </Header>

          {activeTabId === 'HOME' && AI_AGENT_ENABLED && (
            <Box margin={{ top: 'm' }}>
              <AIErrorBoundary>
                <Suspense fallback={null}>
                  <AISearchBar
                    placeholder="Search or Ask a Question"
                    value={aiSearchValue}
                    onChange={(newValue) => {
                      setAiSearchValue(newValue);
                      aiOnQueryChange(newValue);
                    }}
                    onAsk={handleAIAsk}
                    loading={aiIsStreaming}
                    suggestions={aiSuggestions}
                    suggestionsLoading={aiSuggestionsLoading}
                    onClose={aiPopupVisible ? () => setAiPopupVisible(false) : null}
                    onOpen={!aiPopupVisible && selectedSuggestion ? () => setAiPopupVisible(true) : null}
                  />
                </Suspense>
              </AIErrorBoundary>
            </Box>
          )}
          {activeTabId === 'HOME' && AI_AGENT_ENABLED && aiPopupVisible && (
            <div className="dropdown-popup">
              <div className="dropdown-popup-inner">
                <AIErrorBoundary>
                  <Suspense fallback={null}>
                    <SearchPopup onClose={() => setAiPopupVisible(false)} query={aiSearchQuery} searchAnswer={aiSearchAnswer} searchSources={aiSearchSources} quickSearchResults={aiQuickSearchResults} isSearching={aiIsSearching} searchError={aiSearchError} searchSessionId={aiSearchSessionId} searchConversationId={aiSearchConversationId} personaText={aiPersonaText} userName={aiUserName} messages={aiMessages} isStreaming={aiIsStreaming} waitingReason={aiWaitingReason} onChatSubmit={aiHandleChatSubmit} onChatCancel={aiHandleChatCancel} onNewChat={aiHandleNewChat} />
                  </Suspense>
                </AIErrorBoundary>
              </div>
            </div>
          )}

          <Container>

            {addOrEditAccess && activeTabId !== 'AppsLibrary' && tabs?.length > 0 && (
              <Box float="right" margin={{ top: 's', bottom: 's' }}>
                <Link
                  variant="primary"
                  fontSize="heading-m"
                  onFollow={() => setCustomizeTabsVisible(true)}
                >
                  Customize Tabs
                </Link>
              </Box>
            )}
            <br />
            {tabOrderLoaded === true && tabs?.length > 0 ? (<Tabs
              tabs={tabs?.map((tab) => (
                {
                  label: tab?.label,
                  id: tab?.id,
                  disabled: (tab?.id !== 'HOME' && !(tabOrderLoaded)),
                  content: (
                    tab?.tabType === 'BOARD' ? (
                      <CPBoardView
                        pageType={tab?.id}
                        tabType={tab?.tabType}
                        owner={selectedCPView}
                        openResource={updateTab}
                        viewAccess={viewAccess}
                        addOrEditAccess={addOrEditAccess}
                      />
                    ) : tab?.tabType === 'APPS' ? (
                      <Apps closePortalowner={selectedCPView} addOrEditAccess={addOrEditAccess} />
                    ) : tab?.tabType === 'TEAM_APPS' ? (
                      <TeamApplications
                        key={selectedCPView?.value}
                        ldapGroup={selectedCPView?.owner_identifier}
                        teamName={selectedCPView?.label}
                        stage={currentStage}
                      />
                    ) : tab?.tabType === 'LIST' ? (
                      <CustomMultipleResourcePage
                        pageType={tab?.id}
                        tabType={tab?.tabType}
                        owner={selectedCPView}
                        activeHref={activeHref}
                        viewAccess={viewAccess}
                        addOrEditAccess={addOrEditAccess}
                        resetHref={resetHref}
                      />
                    ) : (
                      <></>
                    )
                  ),
                  action: (
                    (isPortalAdmin ||
                      (addOrEditAccess && tab?.id !== 'HOME' && tab?.id !== 'AI/ML')) &&
                    <ButtonDropdown
                      items={[{ id: "hide-tab", text: "hide tab for team" }]}
                      ariaLabel="tab actions"
                      variant="icon"
                      onItemClick={(event) => {
                        if (event?.detail?.id === 'hide-tab') {
                          hideOrUnhideCustomTab(tab?.id, true);
                        }
                      }}
                      expandToViewport={true}
                    />
                  )
                }
              ))}
              activeTabId={activeTabId}
              onChange={(event) => {
                setActiveHref()
                const selectedTab = tabs?.find(t => t.id === event.detail.activeTabId);
                if (event.detail.activeTabId === 'AppsLibrary' || selectedTab?.tabType === 'APPS') {
                  history.push(`/ClosePortal/AppsLibrary/Page/${selectedCPView?.value}`)
                } else {
                  history.push(`/ClosePortal/${event.detail.activeTabId?.replaceAll('/', '%2F')}/Page/${selectedCPView?.value}`)
                }
                setActiveTabId(event.detail.activeTabId)
              }}
            />) : tabOrderLoaded === true && tabs?.length === 0 ? (
              <Box textAlign="center" padding={{ top: "xxl", bottom: "xxl" }}>
                <Box variant="h3" color="text-status-inactive" padding={{ bottom: "s" }}>All tabs are hidden</Box>
                <Box variant="p" color="text-body-secondary" padding={{ bottom: "m" }}>
                  {addOrEditAccess
                    ? 'Use "Customize Tabs" to unhide tabs and configure your workspace.'
                    : 'No tabs are currently visible. Contact your team admin.'}
                </Box>
                {addOrEditAccess && (
                  <Button variant="primary" onClick={() => setCustomizeTabsVisible(true)}>
                    Customize Tabs
                  </Button>
                )}
              </Box>
            ) : <><StatusIndicator colorOverride="blue" type="loading">
                      Fetching Tab Configuration For The Selected Close Portal Team
                    </StatusIndicator></>}
            

            <CreateTeamModal
              visible={visible}
              onDismiss={resetModalParams}
              onCreateTeam={createNewOwner}
              onUpdateTeam={updateTeam}
              isPortalAdmin={isPortalAdmin}
              isSupportAdmin={isSupportAdmin}
              modalLoading={modalLoading}
              errorText={errorText}
              newTeamName={newTeamName}
              setNewTeamName={setNewTeamName}
              newTeamLDAP={newTeamLDAP}
              setNewTeamLDAP={setNewTeamLDAP}
              selectedOrgs={selectedOrgs}
              setSelectedOrgs={setSelectedOrgs}
              selectedRegions={selectedRegions}
              setSelectedRegions={setSelectedRegions}
              selectedVP={selectedVP}
              setSelectedVP={setSelectedVP}
              organizations={organizations}
              regions={regions}
              directReports={directReports}
              getDirectReportsCaller={getDirectReportsCaller}
              availableTeams={cpOwnerOptions?.filter(team => team.owner_type === 'TEAM')}
              selectedTeamToEdit={selectedTeamToEdit}
              setSelectedTeamToEdit={setSelectedTeamToEdit}
              isAteamBI={isAteamBI}
              onLoadTeamDetails={loadTeamDetails}
              onClearError={() => setErrorText('')}
            />

            <CustomizeTabsModal
              visible={customizeTabsVisible}
              onDismiss={() => {
                setCustomizeTabsVisible(false)
                setModalLoading(false)
                setErrorText()
                setSelectedTabControlsSegmentId("add")
                setNewTabName('')
                setSelectedUpdateOrDeleteTab()
                setUpdateTabNameInput('')
                setSelectedTabToUnhide()
              }}
              modalLoading={modalLoading}
              errorText={errorText}
              allTabs={allTabs}
              allCustomTabs={allCustomTabs}
              hiddenTabs={hiddenTabs}
              selectedTabControlsSegmentId={selectedTabControlsSegmentId}
              setSelectedTabControlsSegmentId={setSelectedTabControlsSegmentId}
              newTabName={newTabName}
              setNewTabName={setNewTabName}
              selectedUpdateOrDeleteTab={selectedUpdateOrDeleteTab}
              setSelectedUpdateOrDeleteTab={setSelectedUpdateOrDeleteTab}
              updateTabNameInput={updateTabNameInput}
              setUpdateTabNameInput={setUpdateTabNameInput}
              selectedTabToUnhide={selectedTabToUnhide}
              setSelectedTabToUnhide={setSelectedTabToUnhide}
              onAddTab={addNewCustomTab}
              onUpdateTab={updateExistingCustomTab}
              onDeleteTab={deleteExistingCustomTab}
              selectedTabType={selectedTabType}
              setSelectedTabType={setSelectedTabType}
              onUnhideTab={() => {
                if (selectedTabToUnhide) {
                  hideOrUnhideCustomTab(selectedTabToUnhide?.value, false)
                  resetModalParams()
                } else {
                  setErrorText('Please select a tab to unhide!')
                }
              }}
              visibleTabs={tabs}
              onReorderTabs={handleReorderTabs}
              orderedTabs={orderedTabs}
              selectedCPView={selectedCPView}
            />

          </Container>
        </ContentLayout>


      </Box>
)}
